import { createRouter, createWebHashHistory } from 'vue-router'
import type {
  Router,
  RouteLocationNormalized,
  RouteRecordNormalized,
  RouteRecordRaw
} from 'vue-router'
import { isUrl } from '@/utils/is'
import { omit, cloneDeep } from 'lodash-es'
import { defineAsyncComponent } from 'vue'

const modules = import.meta.glob('../views/**/*.{vue,tsx}')

/**
 * 注册一个异步组件
 * @param componentPath 例:/bpm/oa/leave/detail
 */
export const registerComponent = (componentPath: string) => {
  for (const item in modules) {
    if (item.includes(componentPath)) {
      // 使用异步组件的方式来动态加载组件
      // @ts-ignore
      return defineAsyncComponent(modules[item])
    }
  }
}

/* Layout */
export const Layout = () => import('@/layout/Layout.vue')

export const getParentLayout = () => {
  return () =>
    new Promise((resolve) => {
      resolve({
        name: 'ParentLayout'
      })
    })
}

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

// 前端控制路由生成
export const generateRoutesByFrontEnd = (
  routes: AppRouteRecordRaw[],
  keys: string[],
  basePath = '/'
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const meta = route.meta ?? {}
    // skip some route
    if (meta.hidden && !meta.canTo) {
      continue
    }

    let data: Nullable<AppRouteRecordRaw> = null

    let onlyOneChild: Nullable<string> = null
    if (route.children && route.children.length === 1 && !meta.alwaysShow) {
      onlyOneChild = (
        isUrl(route.children[0].path)
          ? route.children[0].path
          : pathResolve(pathResolve(basePath, route.path), route.children[0].path)
      ) as string
    }

    // 开发者可以根据实际情况进行扩展
    for (const item of keys) {
      // 通过路径去匹配
      if (isUrl(item) && (onlyOneChild === item || route.path === item)) {
        data = Object.assign({}, route)
      } else {
        const routePath = (onlyOneChild ?? pathResolve(basePath, route.path)).trim()
        if (routePath === item || meta.followRoute === item) {
          data = Object.assign({}, route)
        }
      }
    }

    // recursive child routes
    if (route.children && data) {
      data.children = generateRoutesByFrontEnd(
        route.children,
        keys,
        pathResolve(basePath, data.path)
      )
    }
    if (data) {
      res.push(data as AppRouteRecordRaw)
    }
  }
  return res
}

// 后端控制路由生成
export const generateRoutesByServer = (routes: AppCustomRouteRecordRaw[]): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const data: AppRouteRecordRaw = {
      path: route.path,
      name: route.name,
      redirect: route.redirect,
      meta: route.meta
    }
    if (route.component) {
      const comModule = modules[`../${route.component}.vue`] || modules[`../${route.component}.tsx`]
      const component = route.component as string
      if (!comModule && !component.includes('#')) {
        console.error(`未找到${route.component}.vue文件或${route.component}.tsx文件，请创建`)
      } else {
        // 动态加载路由文件，可根据实际情况进行自定义逻辑
        data.component =
          component === '#' ? Layout : component.includes('##') ? getParentLayout() : comModule
      }
    }
    // recursive child routes
    if (route.children) {
      data.children = generateRoutesByServer(route.children)
    }
    res.push(data as AppRouteRecordRaw)
  }
  return res
}

export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) return path
  const childPath = path.startsWith('/') || !path ? path : `/${path}`
  return `${parentPath}${childPath}`.replace(/\/\//g, '/').trim()
}

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const modules: AppRouteRecordRaw[] = cloneDeep(routes)
  for (let index = 0; index < modules.length; index++) {
    const route = modules[index]
    if (!isMultipleRoute(route)) {
      continue
    }
    promoteRouteLevel(route)
  }
  return modules
}

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false
  }

  const children = route.children

  let flag = false
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    if (child.children?.length) {
      flag = true
      break
    }
  }
  return flag
}

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory()
  })

  const routes = router.getRoutes()
  addToChildren(routes, route.children || [], route)
  router = null

  route.children = route.children?.map((item) => omit(item, 'children'))
}

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw
) => {
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    const route = routes.find((item) => item.name === child.name)
    if (!route) {
      continue
    }
    routeModule.children = routeModule.children || []
    if (!routeModule.children.find((item) => item.name === route.name)) {
      routeModule.children?.push(route as unknown as AppRouteRecordRaw)
    }
    if (child.children?.length) {
      addToChildren(routes, child.children, routeModule)
    }
  }
}

// 找出第一个真正可访问的可见路由
export const findFirstVisibleRoute = (routes: any[], isParent: boolean = true): any | null => {
  const find = (list: any[], topRoot?: any, currentFullPath = '/'): any | null => {
    if (!list || !list.length) return null
    const collectedPaths: string[] = []
    let rootRef: any = topRoot

    for (const route of list) {
      if (route?.meta?.hidden) continue

      // 只有在最外层遍历且尚未锁定第一个根路由时，才赋值锁定
      if (!topRoot && !rootRef) {
        rootRef = route
      }
      const resolvedPath = pathResolve(currentFullPath, route.path)

      if (route?.children?.length) {
        const childResult = find(route.children, rootRef, resolvedPath)
        if (childResult?.visibleAllPaths) {
          collectedPaths.push(...childResult.visibleAllPaths)
        }
      } else {
        collectedPaths.push(resolvedPath)
      }
    }

    if (collectedPaths.length > 0) {
      if (isParent) {
        const rootClone = { ...rootRef }
        rootClone.visibleRoutePath = collectedPaths[0]
        rootClone.visibleAllPaths = collectedPaths
        return rootClone
      }
      const findFirstLeaf = (list: any[]): any | null => {
        for (const route of list) {
          if (route?.meta?.hidden) continue
          if (route?.children?.length) {
            const found = findFirstLeaf(route.children)
            if (found) return found
          } else {
            return route
          }
        }
        return null
      }
      return findFirstLeaf(list)
    }

    return null
  }

  return find(routes)
}

// 找出最顶级一层真正可访问的可见路由(接收一个路由数组过滤掉隐藏项:两种情况都要过滤掉 1.只要父隐藏了不管子有没有隐藏都过滤掉 2.父没隐藏但是下面所有的子都隐藏了也要过滤掉.只返回最顶级一层)
// 找出最顶级一层真正可访问的可见路由
export const filterTopLevelVisibleRoutes = (routes: AppRouteRecordRaw[]): AppRouteRecordRaw[] => {
  if (!Array.isArray(routes)) return []
  return routes.filter((route) => {
    // 情况1：只要父隐藏了，直接过滤掉
    if (route?.meta?.hidden) {
      return false
    }

    // 如果存在子路由，递归过滤子路由
    if (route?.children?.length) {
      const visibleChildren = filterTopLevelVisibleRoutes(route.children)
      // 情况2：父没隐藏，但所有子路由都隐藏了，也过滤掉
      if (visibleChildren.length === 0) {
        return false
      }
    }

    return true
  })
}
